import Button from '@ixigo/iui/Button';
import Calendar from '@ixigo/iui/Calendar';
import Popover from '@ixigo/iui/Popover';
import Typography from '@ixigo/iui/Typography';
import {
    ChangeEvent,
    FC,
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
    FormEvent,
    KeyboardEvent,
    FocusEvent,
} from 'react';
import isEmail from 'validator/es/lib/isEmail';
import Turnstile from 'react-turnstile';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import {
    CalenderModifyFilled,
    CallFilled,
    FlightLandingFilled,
    FlightTakeoffFilled,
    MailFilled,
    PersonFilled,
} from '@ixigo/icons';
import CountryCodeSelector from '../CountryCodeSelector';
import {
    BookingFormData,
    CountryPhoneConfig,
    TripType,
    FormErrors,
    GetCitiesParams,
    City,
    GetCitiesResponse,
    GroupBookingSubmissionData,
} from './index.types';
import { getCities } from '../../apis/common';
import { createGroupBooking } from '../../apis/createGroupBooking';
import useUserInfo from '../../hooks/useUserInfo';
import { getCookieHelpers } from '../../utils/cookie';
import { recordEvent } from '../../utils/analytics';
import { Client } from '../../enums/common';
import { useToast } from '../../contexts/ToastContext';
import getCountryFromTimezone from '../CountryCodeSelector/helper';

// India country configuration
const INDIA_COUNTRY: CountryPhoneConfig = {
    name: 'India',
    code: '+91',
    abbreviation: 'in',
    pattern: /^[6-9]\d{9}$/,
    minLength: 10,
    maxLength: 10,
    errorMessage: 'Invalid Indian phone number. Must be 10 digits starting with 6-9',
    placeholder: 'Phone Number',
};

const clientId = getCookieHelpers().getCookie('clientId');

// Country list for selector: { code, name, dialCode } — sorted alphabetically
const getCountryList = (): { code: CountryCode; name: string; dialCode: string }[] => {
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return getCountries()
        .map((code) => ({
            code,
            name: regionNames.of(code) || code,
            dialCode: `+${getCountryCallingCode(code)}`,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
};

const COUNTRY_LIST = getCountryList();

// Empty form used when resetting after submission
const EMPTY_FORM_DATA: BookingFormData = {
    email: '',
    phoneNumber: '',
    tripType: 'oneway',
    travelDate: null,
    returnDate: null,
    numberOfPassengers: undefined,
    comments: '',
    origin: '',
    destination: '',
};

const GroupBookingForm: FC = () => {
    const [showTravelCalendar, setShowTravelCalendar] = useState(false);
    const [showReturnCalendar, setShowReturnCalendar] = useState(false);
    const travelDateInputRef = useRef<HTMLDivElement>(null);
    const returnDateInputRef = useRef<HTMLDivElement>(null);
    const originInputRef = useRef<HTMLInputElement>(null);
    const destinationInputRef = useRef<HTMLInputElement>(null);
    const passengersInputRef = useRef<HTMLInputElement>(null);
    const emailInputRef = useRef<HTMLInputElement>(null);
    const phoneInputRef = useRef<HTMLInputElement>(null);
    const [citiesSuggestion, setCitiesSuggestion] = useState<any[]>([]);
    const [suggestionFor, setSuggestionFor] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [originAirportName, setOriginAirportName] = useState<string>('');
    const [destinationAirportName, setDestinationAirportName] = useState<string>('');
    const [turnstileToken, setTurnstileToken] = useState<string>('');
    const [phoneInputFocused, setPhoneInputFocused] = useState(false);
    const [selectedCountryCode, setSelectedCountryCode] = useState<CountryCode>(
        () => getCountryFromTimezone(COUNTRY_LIST)?.code ?? 'IN',
    );
    const [showCountrySelector, setShowCountrySelector] = useState(false);

    const { userInfo } = useUserInfo();
    const { showToast } = useToast();
    const { email, mobile } = userInfo;
    // Store the valid selected city values to prevent manual editing
    const [validCityValues, setValidCityValues] = useState<{ origin: string | null; destination: string | null }>({
        origin: null,
        destination: null,
    });
    const hideSuggestionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Helper function to get query parameter value
    const getQueryParam = useCallback((paramName: string): string => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(paramName) || '';
    }, []);

    // Parse date string from DDMMYYYY format
    const parseDateFromDDMMYYYY = useCallback((dateStr: string): Date | null => {
        if (!dateStr || dateStr.length !== 8) return null;

        try {
            const day = parseInt(dateStr.substring(0, 2), 10);
            const month = parseInt(dateStr.substring(2, 4), 10) - 1; // Month is 0-indexed
            const year = parseInt(dateStr.substring(4, 8), 10);
            const date = new Date(year, month, day);

            // Validate the date
            if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
                return date;
            }
        } catch (error) {
            // Error parsing date
        }
        return null;
    }, []);

    // Get travelDate and returnDate from URL query parameters (format: DDMMYYYY)
    const parseDateFromQuery = useCallback(
        (paramName: string): Date | null => {
            const dateStr = getQueryParam(paramName);
            return dateStr ? parseDateFromDDMMYYYY(dateStr) : null;
        },
        [getQueryParam, parseDateFromDDMMYYYY],
    );

    // Build initial form from query params only (email/phone filled from userInfo in effect if empty)
    const getInitialFormDataFromQuery = useCallback((): BookingFormData => {
        const emailFromQuery = getQueryParam('email');
        const phoneNumberFromQuery = getQueryParam('phoneNumber');
        const isRoundTrip = getQueryParam('isRoundTrip') === 'true';
        const travelDate = parseDateFromQuery('onwardDate');
        const returnDate = parseDateFromQuery('returnDate');
        const travelDateValid = travelDate && (!returnDate || travelDate <= returnDate);
        const returnDateValid = isRoundTrip && returnDate && travelDate && returnDate >= travelDate;
        return {
            email: emailFromQuery,
            phoneNumber: phoneNumberFromQuery,
            tripType: (isRoundTrip ? 'roundtrip' : 'oneway') as TripType,
            travelDate: travelDateValid ? travelDate : null,
            returnDate: returnDateValid ? (returnDate ?? null) : null,
            numberOfPassengers: undefined,
            comments: '',
            origin: '',
            destination: '',
        };
    }, [getQueryParam, parseDateFromQuery]);

    const initialFormFromQuery = useMemo(() => getInitialFormDataFromQuery(), [getInitialFormDataFromQuery]);

    // Form state — default from query params; email/phone fallback to userInfo applied in effect
    const [formData, setFormData] = useState<BookingFormData>(initialFormFromQuery);
    // Form errors
    const [errors, setErrors] = useState<FormErrors>({});

    const selectedCountry = useMemo(
        () => COUNTRY_LIST.find((c) => c.code === selectedCountryCode),
        [selectedCountryCode],
    );

    // Country list split: "Based on your location" (detected from timezone) + "Alphabetical" (rest)
    const { locationBasedCountries, alphabeticalCountries } = useMemo(() => {
        const detectedOption = getCountryFromTimezone(COUNTRY_LIST);
        const detected = detectedOption?.code ?? null;
        const locationBased = detected ? COUNTRY_LIST.filter((c) => c.code === detected) : [];
        const alphabetical = detected ? COUNTRY_LIST.filter((c) => c.code !== detected) : [...COUNTRY_LIST];
        return { locationBasedCountries: locationBased, alphabeticalCountries: alphabetical };
    }, []);

    // Helper function to extract country name from city string format: "AIRPORT_CODE - CityName, CountryName"
    const extractCountryName = (cityString: string): string | null => {
        if (!cityString || !cityString.includes(',')) return null;
        const parts = cityString.split(',');
        return parts[parts.length - 1]?.trim() || null;
    };

    const formatDisplayDate = (date: Date | null | undefined): string =>
        date ? `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}` : '';

    // Validation functions
    const validateEmail = (emailValue: string, showError: boolean = false): string | undefined => {
        if (!emailValue) return 'Email is required';
        if (!isEmail(emailValue)) {
            if (showError) {
                showToast('Invalid email address', 'alert');
            }
            return 'Invalid email address';
        }
        return undefined;
    };

    const validatePhoneNumber = (phoneNumber: string, showError: boolean = false): string | undefined => {
        if (!phoneNumber) return 'Phone number is required';
        const dialCode = getCountryCallingCode(selectedCountryCode);
        const fullNumber = `+${dialCode}${phoneNumber.replace(/\D/g, '')}`;
        const parsed = parsePhoneNumberFromString(fullNumber, selectedCountryCode);
        if (!parsed || !parsed.isValid()) {
            const message =
                selectedCountryCode === 'IN' ? INDIA_COUNTRY.errorMessage : 'Invalid phone number for selected country';
            if (showError) showToast(message, 'alert');
            return message;
        }
        return undefined;
    };

    const validateOrigin = (origin: string): string | undefined => {
        if (!origin) return 'Origin is required';
        if (!validCityValues.origin || origin !== validCityValues.origin) {
            return 'Select origin from suggestions';
        }
        return undefined;
    };

    const validateDestination = (destination: string): string | undefined => {
        if (!destination) return 'Destination is required';
        if (!validCityValues.destination || destination !== validCityValues.destination) {
            return 'Select destination from suggestions';
        }
        return undefined;
    };

    const validateNumberOfPassengers = (numberOfPassengers: number | undefined): string | undefined => {
        if (numberOfPassengers === undefined || numberOfPassengers === null) return 'Number of passengers is required';
        if (numberOfPassengers < 10) return 'Minimum 10 passengers required';
        if (numberOfPassengers > 1000) return 'Maximum 1000 passengers allowed';
        return undefined;
    };

    const validateTravelDate = (travelDate: Date | null, returnDate?: Date | null): string | undefined => {
        if (!travelDate) return 'Travel date is required';
        if (returnDate && travelDate > returnDate) return 'Travel date must be before return date';
        return undefined;
    };

    const validateReturnDate = (returnDate: Date | null | undefined, travelDate: Date | null): string | undefined => {
        if (!returnDate) return 'Return date is required';
        if (travelDate && returnDate < travelDate) return 'Return date must be after travel date';
        return undefined;
    };

    // Helper function to extract cities array from response
    const extractCities = (response: GetCitiesResponse): City[] => (Array.isArray(response?.data) ? response.data : []);

    // Fetch and set city by airport code
    const fetchAndSetCityByCode = useCallback(async (airportCode: string, fieldName: 'origin' | 'destination') => {
        if (!airportCode) return;

        try {
            const query: GetCitiesParams = {
                searchFor: 'airportSuggestions',
                value: airportCode,
                nearByAirport: 'true',
            };

            const res = await getCities(query);
            const cityArray = extractCities(res);

            const matchedCity = cityArray.find(
                (city: any) => city.airportCode?.toUpperCase() === airportCode.toUpperCase(),
            );

            if (matchedCity) {
                const cityValue = `${matchedCity.airportCode} - ${matchedCity.cityName}, ${matchedCity.countryName}`;
                setFormData((prev) => ({ ...prev, [fieldName]: cityValue }));
                setValidCityValues((prev) => ({
                    ...prev,
                    [fieldName]: cityValue,
                }));
                setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
                if (fieldName === 'origin') {
                    setOriginAirportName(matchedCity.airportName || '');
                } else {
                    setDestinationAirportName(matchedCity.airportName || '');
                }
            }
        } catch (error) {
            // Error fetching city
        }
    }, []);

    // Fill email/phone from userInfo when empty (e.g. not in query); fetch cities from query once on mount
    const hasFetchedCitiesFromQuery = useRef(false);
    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            email: prev.email || email || '',
            phoneNumber: prev.phoneNumber || mobile || '',
        }));

        if (hasFetchedCitiesFromQuery.current) return;
        hasFetchedCitiesFromQuery.current = true;

        const sourceCode = getQueryParam('source');
        const destinationCode = getQueryParam('destination');

        const fetchCities = async () => {
            if (sourceCode) {
                await fetchAndSetCityByCode(sourceCode, 'origin');
            }
            if (destinationCode) {
                await fetchAndSetCityByCode(destinationCode, 'destination');
            }
        };
        fetchCities();
    }, [email, mobile, fetchAndSetCityByCode, getQueryParam]);

    // Debounce timer ref
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Cleanup debounce timer on unmount
    useEffect(
        () => () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        },
        [],
    );

    const onFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!turnstileToken) {
            showToast('Turnstile captcha failed. Please try again.', 'alert');
            return;
        }

        // Validate all fields
        const newErrors: FormErrors = {};
        newErrors.email = validateEmail(formData.email, true);
        newErrors.phoneNumber = validatePhoneNumber(formData.phoneNumber, true);
        newErrors.origin = validateOrigin(formData.origin);
        newErrors.destination = validateDestination(formData.destination);
        newErrors.numberOfPassengers = validateNumberOfPassengers(formData.numberOfPassengers);
        newErrors.travelDate = validateTravelDate(formData.travelDate, formData.returnDate);

        if (formData.tripType === 'roundtrip') {
            newErrors.returnDate = validateReturnDate(formData.returnDate, formData.travelDate);
        }

        setErrors(newErrors);

        // Check if there are any errors
        const hasErrors = Object.values(newErrors).some((error) => error !== undefined);
        if (hasErrors) {
            // Focus first field with error - use setTimeout to ensure state is updated
            setTimeout(() => {
                if (newErrors.origin) {
                    originInputRef.current?.focus();
                } else if (newErrors.destination) {
                    destinationInputRef.current?.focus();
                } else if (newErrors.travelDate) {
                    setShowTravelCalendar(true);
                } else if (newErrors.returnDate) {
                    setShowReturnCalendar(true);
                } else if (newErrors.numberOfPassengers) {
                    passengersInputRef.current?.focus();
                } else if (newErrors.email) {
                    emailInputRef.current?.focus();
                } else if (newErrors.phoneNumber) {
                    phoneInputRef.current?.focus();
                }
            }, 0);
            return;
        }

        // Map trip type to payload format
        const tripTypeMap: Record<TripType, string> = {
            oneway: 'One Way',
            roundtrip: 'Round Trip',
        };

        let isSuccess = true;
        setLoading(true);

        try {
            const originCountry = extractCountryName(formData.origin);
            const destinationCountry = extractCountryName(formData.destination);
            const isInternational = originCountry !== 'India' || destinationCountry !== 'India';

            // Extract phone number with country code
            const dialCode = getCountryCallingCode(selectedCountryCode);
            const phone = {
                countryCode: selectedCountryCode.toLowerCase(),
                dialCode,
                number: formData.phoneNumber,
            };

            // Format mobile number as "+xx xxxxx-xxxxx" (India) or "+xx ..." (others)
            const formatMobileNumber = (dial: string, phoneNumber: string): string => {
                if (!phoneNumber) return '';
                if (selectedCountryCode === 'IN' && phoneNumber.length >= 5) {
                    const firstFive = phoneNumber.slice(0, 5);
                    const remaining = phoneNumber.slice(5);
                    return `+${dial} ${firstFive}-${remaining}`;
                }
                return `+${dial} ${phoneNumber}`;
            };

            // Prepare submission data
            const submissionData: GroupBookingSubmissionData = {
                email: formData.email,
                mobileNumber: formatMobileNumber(dialCode, formData.phoneNumber),
                origin: formData.origin,
                destination: formData.destination,
                passengersNumber: formData.numberOfPassengers,
                preference: formData.comments || '',
                tripType: tripTypeMap[formData.tripType],
                travelDate: formData.travelDate,
                returnDate: formData.returnDate || null,
                phone,
                isInternational,
                clientid: String(clientId),
                turnstileToken,
            };
            // Submit to API
            try {
                const response = await createGroupBooking(submissionData);
                if (response.status === 'success') {
                    showToast('Group booking submitted successfully', 'success');
                    // Reset form to empty and turnstile token on success
                    setFormData(EMPTY_FORM_DATA);
                    setErrors({});
                    setOriginAirportName('');
                    setDestinationAirportName('');
                    setValidCityValues({ origin: null, destination: null });
                    setTurnstileToken('');
                } else {
                    showToast('Failed to submit group booking', 'alert');
                }
            } catch (error) {
                showToast('Failed to submit group booking', 'alert');
            }
            // showToast('Group booking submitted successfully', 'success');
            // Track event
            recordEvent('Flight - Group Lead Form', {
                additionalParams: {
                    'Email Address': formData.email,
                    'Mobile Number': `${selectedCountry?.dialCode || '+91'}${formData.phoneNumber}`,
                    Email: formData.email,
                    'Trip Type': tripTypeMap[formData.tripType],
                    'Travel Date': formData.travelDate ? formData.travelDate.toISOString() : '',
                    'Return Date': formData.returnDate ? formData.returnDate.toISOString() : '',
                    Origin: formData.origin,
                    Destination: formData.destination,
                    Pax: String(formData.numberOfPassengers),
                    Comments: formData.comments || '',
                    URL: window.location.origin,
                    success: String(isSuccess),
                    clientid: clientId,
                },
            });
        } catch (error) {
            isSuccess = false;
            // Error submitting group booking

            // Track failed event
            recordEvent('Flight - Group Lead Form', {
                additionalParams: {
                    'Email Address': formData.email,
                    'Mobile Number': `${selectedCountry?.dialCode || '+91'}${formData.phoneNumber}`,
                    Email: formData.email,
                    'Trip Type': tripTypeMap[formData.tripType],
                    'Travel Date': formData.travelDate ? formData.travelDate.toISOString() : '',
                    'Return Date': formData.returnDate ? formData.returnDate.toISOString() : '',
                    Origin: formData.origin,
                    Destination: formData.destination,
                    Pax: String(formData.numberOfPassengers),
                    Comments: formData.comments || '',
                    URL: window.location.origin,
                    success: String(isSuccess),
                    clientid: clientId || 'n/a',
                },
            });
        } finally {
            setLoading(false);
        }
    };

    const getCitiesList = useCallback(
        (event: ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>) => {
            if (hideSuggestionsTimeoutRef.current) {
                clearTimeout(hideSuggestionsTimeoutRef.current);
                hideSuggestionsTimeoutRef.current = null;
            }
            const target = event.target as HTMLInputElement;
            const id = target.id as 'origin' | 'destination';
            const { value } = target;

            setSuggestionFor(id);

            // Clear previous timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // Set up debounce
            debounceTimerRef.current = setTimeout(() => {
                let query: GetCitiesParams = { searchFor: 'popularAirport' };

                if (value) {
                    query = {
                        searchFor: 'airportSuggestions',
                        value,
                        nearByAirport: 'true',
                    };
                }

                setCitiesSuggestion([]);

                getCities(query)
                    .then((res) => {
                        const cities = extractCities(res);
                        setCitiesSuggestion(cities);
                    })
                    .catch(() => {
                        // Error fetching cities
                        setCitiesSuggestion([]);
                    });
            }, 300);
        },
        [],
    );

    const hideSuggestionsList = useCallback(() => {
        if (hideSuggestionsTimeoutRef.current) {
            clearTimeout(hideSuggestionsTimeoutRef.current);
            hideSuggestionsTimeoutRef.current = null;
        }
        setSuggestionFor('');
        setCitiesSuggestion([]);
    }, []);

    const onCitySelect = useCallback(
        (city: any) => {
            const cityValue = `${city.airportCode} - ${city.cityName}, ${city.countryName}`;
            const fieldName = suggestionFor as 'origin' | 'destination';
            if (fieldName === 'origin') {
                setOriginAirportName(city.airportName);
            } else {
                setDestinationAirportName(city.airportName);
            }
            setFormData((prev) => ({ ...prev, [fieldName]: cityValue }));
            setValidCityValues((prev) => ({
                ...prev,
                [fieldName]: cityValue,
            }));
            setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
            hideSuggestionsList();
        },
        [suggestionFor, hideSuggestionsList],
    );

    const renderSuggestionsList = () => {
        if (!citiesSuggestion?.length || !suggestionFor) return null;

        let selectedAirportCode = '';
        const { origin, destination } = formData;

        if (origin && suggestionFor === 'destination') {
            [selectedAirportCode] = origin.split(' - ');
        } else if (destination && suggestionFor === 'origin') {
            [selectedAirportCode] = destination.split(' - ');
        }

        return (
            <ul
                className='absolute z-50 left-0 top-full mt-2 shadow-[0_0_25px_0_rgba(0,0,0,0.3)] bg-primary min-w-[280px] max-h-[300px] overflow-y-auto rounded-10'
                style={{ position: 'absolute' }}
            >
                {citiesSuggestion
                    .filter((item: any) => item.airportCode !== selectedAirportCode)
                    .map((item: any) => (
                        <li
                            key={item.airportCode}
                            className='flex justify-between p-15 border-charcoal hover:border-brand hover:bg-seville-50 cursor-pointer'
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => onCitySelect(item)}
                        >
                            <div className='flex flex-col'>
                                <Typography variant='body-sm'>
                                    {item.airportCode} - {item.cityName}, {item.countryName}
                                </Typography>
                                <Typography variant='body-xs' className='text-charcoal-600'>
                                    {item.airportName}
                                </Typography>
                            </div>
                            {item.nearByAirport && (
                                <Typography variant='body-xs' className='text-charcoal-600'>
                                    near {item.nearByCity?.name}
                                </Typography>
                            )}
                        </li>
                    ))}
            </ul>
        );
    };

    return (
        <div className='flex-1 rounded-[20px] shadow-300 lg:max-w-[600px] md:w-full bg-white'>
            <form
                id='group-booking-form'
                onSubmit={onFormSubmit}
                noValidate
                className='flex flex-col p-10 md:p-30 overflow-visible'
            >
                {/* Trip Type */}
                <div className='flex items-center justify-center gap-3 p-2 mb-5 lg:w-300 lg:mx-auto'>
                    <button
                        type='button'
                        onClick={() => {
                            setFormData((prev) => ({ ...prev, tripType: 'oneway', returnDate: null }));
                            setErrors((prev) => ({ ...prev, returnDate: undefined }));
                        }}
                        className={`px-4 lg:py-2 md:py-1 rounded-full transition-all outline-none ${
                            formData.tripType === 'oneway'
                                ? 'bg-black text-white'
                                : 'bg-transparent text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        One Way
                    </button>
                    <button
                        type='button'
                        onClick={() => setFormData((prev) => ({ ...prev, tripType: 'roundtrip' }))}
                        className={`px-4 lg:py-2 md:py-1 rounded-full transition-all outline-none ${
                            formData.tripType === 'roundtrip'
                                ? 'bg-black text-white'
                                : 'bg-transparent text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        Round Trip
                    </button>
                </div>
                <div className='w-full border border-b-0 overflow-visible divide-y rounded-t-[20px]'>
                    {/* Origin and Destination */}
                    <div className='flex flex-col lg:flex-row divide-gray-200 overflow-visible lg:divide-x lg:divide-y-0 divide-y '>
                        <div className='w-full lg:w-1/2 h-[50px] flex gap-2 pl-2 pr-[2px] items-center relative'>
                            <FlightTakeoffFilled className='shrink-0 text-gray-700' />
                            <div className='flex-1 min-w-0 overflow-hidden'>
                                <input
                                    ref={originInputRef}
                                    id='origin'
                                    value={formData.origin}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        const currentValue = e.target.value;
                                        setFormData((prev) => ({ ...prev, origin: currentValue }));
                                        if (validCityValues.origin && currentValue !== validCityValues.origin) {
                                            setValidCityValues((prev) => ({ ...prev, origin: null }));
                                            setOriginAirportName('');
                                        }
                                        if (errors.origin) {
                                            setErrors((prev) => ({ ...prev, origin: validateOrigin(currentValue) }));
                                        }
                                        getCitiesList(e);
                                    }}
                                    onBlur={() => {
                                        setErrors((prev) => ({ ...prev, origin: validateOrigin(formData.origin) }));
                                        hideSuggestionsTimeoutRef.current = setTimeout(() => {
                                            hideSuggestionsList();
                                            hideSuggestionsTimeoutRef.current = null;
                                        }, 200);
                                    }}
                                    onKeyUp={(e) => getCitiesList(e)}
                                    onFocus={(e) => getCitiesList(e)}
                                    autoComplete='off'
                                    placeholder='Enter origin'
                                    className='block w-full h-full border-none bg-transparent  outline-none focus:border-none focus:outline-none overflow-hidden text-ellipsis whitespace-nowrap'
                                />
                                <span className='text-sm truncate block text-[#81838b]'> {originAirportName}</span>
                            </div>
                            {suggestionFor === 'origin' && renderSuggestionsList()}
                        </div>
                        <div className='w-full lg:w-1/2 h-[50px] flex gap-2 pl-2 pr-[3px] items-center relative'>
                            <FlightLandingFilled className='shrink-0 text-gray-700' />
                            <div className='flex-1 min-w-0 overflow-hidden'>
                                <input
                                    ref={destinationInputRef}
                                    id='destination'
                                    value={formData.destination}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        const currentValue = e.target.value;
                                        setFormData((prev) => ({ ...prev, destination: currentValue }));
                                        if (
                                            validCityValues.destination &&
                                            currentValue !== validCityValues.destination
                                        ) {
                                            setValidCityValues((prev) => ({ ...prev, destination: null }));
                                            setDestinationAirportName('');
                                        }
                                        if (errors.destination) {
                                            setErrors((prev) => ({
                                                ...prev,
                                                destination: validateDestination(currentValue),
                                            }));
                                        }
                                        getCitiesList(e);
                                    }}
                                    onBlur={() => {
                                        setErrors((prev) => ({
                                            ...prev,
                                            destination: validateDestination(formData.destination),
                                        }));
                                        hideSuggestionsTimeoutRef.current = setTimeout(() => {
                                            hideSuggestionsList();
                                            hideSuggestionsTimeoutRef.current = null;
                                        }, 200);
                                    }}
                                    onKeyUp={(e) => getCitiesList(e)}
                                    onFocus={(e) => getCitiesList(e)}
                                    autoComplete='off'
                                    placeholder='Enter destination'
                                    className='block w-full h-full border-none bg-transparent pb-[2px] outline-none focus:border-none focus:outline-none overflow-hidden text-ellipsis whitespace-nowrap'
                                />
                                <span className='text-sm truncate block text-[#81838b]'> {destinationAirportName}</span>
                            </div>
                            {suggestionFor === 'destination' && renderSuggestionsList()}
                        </div>
                    </div>

                    {/* Travel Dates */}
                    <div className='flex items-center border-t flex-row divide-x divide-gray-200'>
                        <div className='h-[50px] flex items-center gap-2 px-2 w-1/2 min-w-0'>
                            <CalenderModifyFilled className='shrink-0 text-gray-700' />
                            <div ref={travelDateInputRef} className='flex-1 h-full flex items-center min-w-0'>
                                <input
                                    value={formatDisplayDate(formData.travelDate)}
                                    placeholder='Departure'
                                    className='flex-1 h-full border-none outline-none focus:border-none focus:outline-none cursor-pointer min-w-0'
                                    readOnly
                                    onClick={() => setShowTravelCalendar(true)}
                                />
                            </div>
                            <Popover
                                show={showTravelCalendar}
                                onClose={() => setShowTravelCalendar(false)}
                                referenceRef={travelDateInputRef}
                                placement='bottom-end'
                            >
                                <div className='border border-neutral-100 rounded-10 overflow-hidden lg:max-w-[400px]'>
                                    <Calendar
                                        onChange={(value: Date | Date[] | null) => {
                                            if (value instanceof Date) {
                                                setFormData((prev) => ({ ...prev, travelDate: value }));
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    travelDate: validateTravelDate(value, formData.returnDate),
                                                }));
                                                setShowTravelCalendar(false);
                                            } else if (Array.isArray(value) && value.length > 0) {
                                                setFormData((prev) => ({ ...prev, travelDate: value[0] }));
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    travelDate: validateTravelDate(value[0], formData.returnDate),
                                                }));
                                                setShowTravelCalendar(false);
                                            }
                                        }}
                                        value={formData.travelDate || undefined}
                                        minDate={new Date()}
                                        maxDate={
                                            formData.tripType === 'roundtrip' && formData.returnDate
                                                ? formData.returnDate
                                                : undefined
                                        }
                                        selectRange={false}
                                    />
                                </div>
                            </Popover>
                        </div>
                        {formData.tripType === 'roundtrip' &&
                            (() => {
                                // Calculate placement based on clientId
                                let returnCalendarPlacement: 'bottom' | 'bottom-end' = 'bottom';
                                if (clientId === Client.IXIWEB) {
                                    returnCalendarPlacement = 'bottom-end';
                                }

                                return (
                                    <div className='h-[50px] w-1/2 flex flex-row items-center gap-2 px-2 min-w-0'>
                                        <CalenderModifyFilled className='shrink-0 text-gray-700' />
                                        <div
                                            ref={returnDateInputRef}
                                            className='flex-1 h-full flex items-center min-w-0'
                                        >
                                            <input
                                                value={formatDisplayDate(formData.returnDate)}
                                                placeholder='Return'
                                                className='flex-1 h-full border-none outline-none focus:border-none focus:outline-none cursor-pointer min-w-0'
                                                readOnly
                                                onClick={() => setShowReturnCalendar(true)}
                                            />
                                        </div>
                                        <Popover
                                            show={showReturnCalendar}
                                            onClose={() => setShowReturnCalendar(false)}
                                            referenceRef={returnDateInputRef}
                                            placement={returnCalendarPlacement}
                                        >
                                            <div className='border border-neutral-100 rounded-10 overflow-hidden lg:max-w-[400px] md:max-w-[300px]'>
                                                <Calendar
                                                    onChange={(value: Date | Date[] | null) => {
                                                        if (value instanceof Date) {
                                                            setFormData((prev) => ({ ...prev, returnDate: value }));
                                                            setErrors((prev) => ({
                                                                ...prev,
                                                                returnDate: validateReturnDate(
                                                                    value,
                                                                    formData.travelDate,
                                                                ),
                                                                travelDate: validateTravelDate(
                                                                    formData.travelDate,
                                                                    value,
                                                                ),
                                                            }));
                                                            setShowReturnCalendar(false);
                                                        } else if (Array.isArray(value) && value.length > 0) {
                                                            setFormData((prev) => ({ ...prev, returnDate: value[0] }));
                                                            setErrors((prev) => ({
                                                                ...prev,
                                                                returnDate: validateReturnDate(
                                                                    value[0],
                                                                    formData.travelDate,
                                                                ),
                                                                travelDate: validateTravelDate(
                                                                    formData.travelDate,
                                                                    value[0],
                                                                ),
                                                            }));
                                                            setShowReturnCalendar(false);
                                                        }
                                                    }}
                                                    value={formData.returnDate || undefined}
                                                    minDate={formData.travelDate || new Date()}
                                                    selectRange={false}
                                                />
                                            </div>
                                        </Popover>
                                    </div>
                                );
                            })()}
                    </div>
                    <div className='flex flex-row items-center gap-2 h-[50px]  px-2 '>
                        <PersonFilled className='shrink-0 text-gray-700' />
                        <input
                            ref={passengersInputRef}
                            type='number'
                            min='8'
                            value={formData.numberOfPassengers || ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const inputValue = e.target.value;
                                const parsedValue = inputValue ? parseInt(inputValue, 10) : undefined;
                                const finalValue =
                                    parsedValue !== undefined && !Number.isNaN(parsedValue) ? parsedValue : undefined;

                                setFormData((prev) => ({ ...prev, numberOfPassengers: finalValue }));

                                if (errors.numberOfPassengers && finalValue !== undefined) {
                                    const validationResult = validateNumberOfPassengers(finalValue);
                                    if (!validationResult) {
                                        setErrors((prev) => ({ ...prev, numberOfPassengers: undefined }));
                                    }
                                }
                            }}
                            onBlur={() => {
                                const validationResult = validateNumberOfPassengers(formData.numberOfPassengers);
                                setErrors((prev) => ({ ...prev, numberOfPassengers: validationResult }));
                            }}
                            placeholder='Number of passengers'
                            className='flex-1 h-full border-none outline-none focus:border-none focus:outline-none'
                        />
                    </div>
                    <div className='flex items-center border-t  flex-col lg:flex-row divide-y lg:divide-y-0 lg:divide-x divide-gray-200'>
                        <div className='w-full lg:w-1/2 h-[50px] flex flex-row items-center gap-2 px-2 '>
                            <MailFilled className='shrink-0 text-gray-700' />
                            <input
                                ref={emailInputRef}
                                value={formData.email}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    const currentValue = e.target.value;
                                    setFormData((prev) => ({ ...prev, email: currentValue }));
                                    if (errors.email) {
                                        setErrors((prev) => ({ ...prev, email: validateEmail(currentValue) }));
                                    }
                                }}
                                placeholder='Enter email'
                                className='flex-1 h-full border-none outline-none focus:border-none focus:outline-none'
                            />
                        </div>

                        <div className='w-full lg:w-1/2 h-[50px] flex flex-row items-center px-2 '>
                            <CallFilled className='shrink-0 text-gray-700 mr-2' />
                            <div className='flex-1 h-full flex items-center gap-1 min-w-0'>
                                <button
                                    type='button'
                                    onClick={() => setShowCountrySelector(true)}
                                    className='flex items-center gap-1 shrink-0 text-gray-700 h-full pr-1 outline-none focus:outline-none hover:opacity-80'
                                    aria-label='Select country code'
                                >
                                    <span className='text-inherit'>
                                        {selectedCountry ? `${selectedCountry.dialCode}` : '+91'}
                                    </span>
                                </button>
                                <CountryCodeSelector
                                    open={showCountrySelector}
                                    onClose={() => setShowCountrySelector(false)}
                                    selectedCountryCode={selectedCountryCode}
                                    onSelect={(code) => {
                                        setSelectedCountryCode(code);
                                        setShowCountrySelector(false);
                                    }}
                                    locationBasedCountries={locationBasedCountries}
                                    alphabeticalCountries={alphabeticalCountries}
                                />
                                <input
                                    ref={phoneInputRef}
                                    value={formData.phoneNumber}
                                    maxLength={15}
                                    onFocus={() => setPhoneInputFocused(true)}
                                    onBlur={() => setPhoneInputFocused(false)}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        const digitsOnly = e.target.value.replace(/\D/g, '');
                                        setFormData((prev) => ({ ...prev, phoneNumber: digitsOnly }));
                                        if (errors.phoneNumber) {
                                            setErrors((prev) => ({
                                                ...prev,
                                                phoneNumber: validatePhoneNumber(digitsOnly),
                                            }));
                                        }
                                    }}
                                    placeholder={phoneInputFocused || formData.phoneNumber ? '' : 'Enter phone number'}
                                    className='flex-1 min-w-0 h-full border-none outline-none focus:border-none focus:outline-none'
                                />
                            </div>
                        </div>
                    </div>

                    {/* Additional Comments / Details */}
                    <div className='flex flex-col gap-5 border-t border-gray-200 p-1'>
                        <textarea
                            value={formData.comments || ''}
                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                                setFormData((prev) => ({ ...prev, comments: e.target.value }));
                            }}
                            placeholder='Add any additional details or special requirements about your trip.'
                            rows={4}
                            className='border-none outline-none focus:border-none focus:outline-none resize-none p-2'
                        />
                    </div>
                </div>

                <Turnstile
                    sitekey='0x4AAAAAAABFflgN8--6MP2V'
                    onVerify={(token: string) => {
                        if (token) setTurnstileToken(token);
                    }}
                />
                <Button
                    type='submit'
                    form='group-booking-form'
                    className='w-full h-[60px] text-lg outline-none rounded-none rounded-b-[20px] text-white font-medium'
                    disabled={loading}
                >
                    {loading ? 'Submitting...' : 'Submit'}
                </Button>
            </form>
        </div>
    );
};

export default GroupBookingForm;
